const stateNameToId = {
	pendiente: 1,
	aprobada: 2,
	rechazada: 3,
	"en proceso": 4,
	finalizada: 5,
	sincronizada: 6,
};

const getStateId = (stateName) => {
	const key = String(stateName || "")
		.trim()
		.toLowerCase();
	return stateNameToId[key] || 6;
};

const toUniqueText = (rows, key) => {
	const values = rows.map((row) => row[key]).filter(Boolean);
	return [...new Set(values)].join(", ");
};

const normalizeDetailOptions = (options = []) => {
	if (!Array.isArray(options)) {
		return [];
	}

	return options.map((option) => {
		if (typeof option === "string") {
			return { product_name: option };
		}
		return {
			product_name: option?.product_name || "",
		};
	});
};

const normalizeTooltip = (tooltip) => {
	if (!tooltip || typeof tooltip !== "object") {
		return null;
	}

	const details = Array.isArray(tooltip.details) ? tooltip.details : [];

	return {
		head: {
			percent_used: Number(tooltip?.head?.percent_used ?? 0),
			percent_pending: Number(tooltip?.head?.percent_pending ?? 0),
		},
		details: details.map((detail) => ({
			product: detail?.product || "",
			unit_measurement: detail?.unit_measurement || "",
			quantity: Number(detail?.quantity ?? 0),
			total_out: Number(detail?.total_out ?? 0),
			total_used: Number(detail?.total_used ?? 0),
			total_pending: Number(detail?.total_pending ?? 0),
			has_active_optionals: Boolean(detail?.has_active_optionals),
			optional_products: Array.isArray(detail?.optional_products)
				? detail.optional_products.map((optionalProduct) => ({
						product: optionalProduct?.product || "",
						unit_measurement: optionalProduct?.unit_measurement || "",
						quantity: Number(optionalProduct?.quantity ?? 0),
						total_out: Number(optionalProduct?.total_out ?? 0),
						total_used: Number(optionalProduct?.total_used ?? 0),
						total_pending: Number(optionalProduct?.total_pending ?? 0),
					}))
				: [],
		})),
	};
};

export function useOADataTransform(rows = []) {
	return rows.map((row) => {
		const businessCenters = Array.isArray(row.business_center) ? row.business_center : [];
		const machineries = Array.isArray(row.machineries) ? row.machineries : [];
		const details = Array.isArray(row.details) ? row.details : [];
		const normalizedDetails = details.map((detail) => ({
			detail_id: detail.detail_id,
			product_id: detail.product_id,
			product_name: detail.product_name,
			product_code: detail.product_code,
			inventory_codes: detail.inventory_codes || "",
			active_ingredient_text: detail.active_ingredient_text,
			objective_name: detail.objective_name,
			dose_100l: detail.dose_100l,
			dose_100L: detail.dose_100L,
			dose_ha: detail.dose_ha,
			quantity: detail.quantity,
			quantity_output: Number(detail.quantity_output ?? 0),
			quantity_used: Number(detail.quantity_used ?? 0),
			quantity_machinery: detail.quantity_machinery,
			unit_measurement: detail.unit_measurement || "",
			machined: detail.machined,
			is_copy: detail.is_copy ?? detail.isCopy ?? 0,
			options: normalizeDetailOptions(detail.options),
			price_local: Number(detail.price_local ?? 0),
			price_local_oa: Number(detail.price_local_oa ?? 0),
		}));
		const totalWetness = machineries.reduce((accumulator, item) => accumulator + Number(item.wetness || 0), 0);
		const averageWetness = machineries.length > 0 ? totalWetness / machineries.length : 0;
		const totalMachinedLiters = machineries.reduce((accumulator, item) => accumulator + Number(item.machined || 0), 0);
		const normalizedBusinessCenters = businessCenters.map((businessCenter) => ({
			id: businessCenter.id,
			business_center_parent_name: businessCenter.business_center_parent_name || "",
			name: businessCenter.name || "",
			specie: businessCenter.specie || "",
			variety: businessCenter.variety || "",
			area_ha: Number(businessCenter.area_ha || 0),
		}));

		return {
			id: Number(row.id || 0),
			app_order_id: Number(row.id || 0),
			code: row.code || "-",
			application_order_date: row.application_order_date || row.created_date || "",
			estate_parent_name: row.field_name || "-",
			application_orders_state_name: row.state_name || "Sin estado",
			application_orders_state_id: Number(row.state_id || getStateId(row.state_name)),
			application_order_type_name: row.application_order_type_name || "-",
			application_order_type_customer_name: row.application_order_type_customer_name || row.application_order_type_name || "-",
			application_order_type_id_customer: Number(row.application_order_type_id_customer || row.application_order_type_id || 0),
			application_order_type_id: Number(row.application_order_type_id || 0),
			business_name: row.business_name || "-",
			business_centers: normalizedBusinessCenters,
			business_center_names: toUniqueText(businessCenters, "name"),
			business_center_parents_name: toUniqueText(businessCenters, "business_center_parent_name"),
			specie: toUniqueText(businessCenters, "specie"),
			variety: toUniqueText(businessCenters, "variety"),
			areas_sum: businessCenters.reduce((accumulator, item) => accumulator + Number(item.area_ha || 0), 0),
			application_orders_machineries_wetness: averageWetness,
			application_orders_machineries_machined: totalMachinedLiters,
			calculated_machined: Boolean(row.calculated_machined),
			products_count: details.length,
			details: normalizedDetails,
			tooltip: normalizeTooltip(row.tooltip),
			internal_code: row.internal_code || "-",
			documents: Array.isArray(row.documents) ? row.documents : [],
		};
	});
}
